// services/locationService.ts
import * as geofire from 'geofire-common';
import { 
  collection, 
  query, 
  orderBy, 
  startAt, 
  endAt, 
  Query, 
  QueryConstraint 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Location, LocationFormData, Coordinates } from '../types/location';

export class LocationServiceError extends Error {
  constructor(
    message: string,
    public code: 'GEOCODING_FAILED' | 'INVALID_COORDINATES' | 'QUERY_FAILED',
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'LocationServiceError';
  }
}

export class LocationService {
  private static readonly MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  /**
   * Convert a LocationFormData into a full Location with coordinates and geohash
   * Geocoding is optional - if it fails, location is created without coordinates
   */
  static async createLocation(formData: LocationFormData): Promise<Location> {
    try {
      const coordinates = await this.getCoordinates(formData);
      const geohash = this.generateGeohash(coordinates);

      return {
        ...formData,
        coordinates,
        geohash
      };
    } catch (error) {
      // Geocoding failed - return location without coordinates
      // This is acceptable for promoter-only platform where geo-search isn't needed
      console.warn('Geocoding failed, creating location without coordinates:', error);
      return {
        ...formData,
        coordinates: { latitude: 0, longitude: 0 },
        geohash: ''
      };
    }
  }

  /**
   * Update an existing location with new form data, only geocoding if necessary
   */
  static async updateLocation(
    currentLocation: Location,
    updates: Partial<LocationFormData>
  ): Promise<Location> {
    // If no location fields are being updated, return current location
    if (!updates.city && !updates.state && !updates.country) {
      return currentLocation;
    }

    // Create new location data mixing current and updates
    const newLocationData: LocationFormData = {
      city: updates.city || currentLocation.city,
      state: updates.state || currentLocation.state,
      country: updates.country || currentLocation.country
    };

    return await this.createLocation(newLocationData);
  }

  /**
   * Get coordinates for a location using Google Maps Geocoding API
   */
  private static async getCoordinates(location: LocationFormData): Promise<Coordinates> {
    try {
      const address = `${location.city}, ${location.state || ''}, ${location.country}`.trim();
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=${this.MAPS_API_KEY}`
      );
      
      const data = await response.json();
      
      if (!data.results?.[0]?.geometry?.location) {
        throw new LocationServiceError(
          'No coordinates found for address',
          'GEOCODING_FAILED'
        );
      }

      const { lat, lng } = data.results[0].geometry.location;
      return { latitude: lat, longitude: lng };
    } catch (error) {
      if (error instanceof LocationServiceError) throw error;
      throw new LocationServiceError(
        'Failed to get coordinates',
        'GEOCODING_FAILED',
        error
      );
    }
  }

  /**
   * Generate a geohash for given coordinates
   */
  static generateGeohash(coordinates: Coordinates): string {
    return geofire.geohashForLocation([
      coordinates.latitude,
      coordinates.longitude
    ]);
  }

  /**
   * Calculate distance between two coordinates in miles
   */
  static calculateDistance(point1: Coordinates, point2: Coordinates): number {
    const distanceInKm = geofire.distanceBetween(
      [point1.latitude, point1.longitude],
      [point2.latitude, point2.longitude]
    );
    return this.kmToMiles(distanceInKm);
  }

  /**
   * Get the geohash query bounds for a radius search
   * Returns array of [startHash, endHash] pairs
   */
  static getQueryBounds(center: Coordinates, radiusInMiles: number): string[][] {
    const radiusInKm = this.milesToKm(radiusInMiles);
    return geofire.geohashQueryBounds(
      [center.latitude, center.longitude],
      radiusInKm
    );
  }

  /**
   * Create Firestore queries for a geohash range search
   */
  static createGeohashQueries(
    collectionPath: string,
    bounds: string[][],
    additionalConstraints: QueryConstraint[] = []
  ): Query[] {
    return bounds.map(([start, end]) =>
      query(
        collection(db, collectionPath),
        ...additionalConstraints,
        orderBy('geohash'),
        startAt(start),
        endAt(end)
      )
    );
  }

  /**
   * Check if a document is within a given radius
   * Useful for filtering out false positives from geohash queries
   */
  static isWithinRadius(
    location: Location,
    center: Coordinates,
    radiusInMiles: number
  ): boolean {
    const distance = this.calculateDistance(location.coordinates, center);
    return distance <= radiusInMiles;
  }

  private static milesToKm(miles: number): number {
    return miles * 1.60934;
  }

  private static kmToMiles(km: number): number {
    return km * 0.621371;
  }
}